import React, { useEffect, useState, useCallback, useMemo } from "react";
import toast from "react-hot-toast";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import {
  fetchPendingBillsOldestFirst,
  applyPaymentCombined,
  allocateFIFOAfterManual,
  daysOutstandingForBill,
  creditBillBranch,
} from "../../services/creditDueService";

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

/**
 * Shared form for receiving payment — use inside a page or modal.
 * Per-bill amounts are applied first; the FIFO pool then fills remaining balances by oldest invoice date first.
 * You can use either, or both in one receipt.
 *
 * @param {{ initialCustomerId?: string, onSuccess?: () => void, branch?: string }} props
 */
export default function CreditReceivePaymentContent({ initialCustomerId = "", onSuccess, branch = "Main" }) {
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState(initialCustomerId);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState("");
  /** cash | card_upi — how the customer paid */
  const [payMode, setPayMode] = useState("cash");

  const [fifoAmount, setFifoAmount] = useState("");
  const [manual, setManual] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const loadCustomers = useCallback(async () => {
    try {
      const snap = await getDocs(query(collection(db, "customers"), where("group", "==", "Sundry Debtors")));
      setCustomers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error(e);
      toast.error("Could not load customers.");
    }
  }, []);

  const loadPending = useCallback(async () => {
    if (!customerId) {
      setPending([]);
      setManual({});
      return;
    }
    setLoading(true);
    try {
      const rows = (await fetchPendingBillsOldestFirst(customerId)).filter((b) => creditBillBranch(b) === branch);
      setPending(rows);
      const next = {};
      rows.forEach((b) => {
        next[b.id] = "";
      });
      setManual(next);
    } catch (e) {
      toast.error(e?.message || "Failed to load bills.");
    } finally {
      setLoading(false);
    }
  }, [customerId, branch]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  useEffect(() => {
    setCustomerId(initialCustomerId || "");
  }, [initialCustomerId]);

  useEffect(() => {
    loadPending();
  }, [loadPending]);

  const manualTotal = useMemo(() => {
    let s = 0;
    for (const b of pending) {
      const v = parseFloat(manual[b.id] || "0");
      if (Number.isFinite(v)) s += v;
    }
    return round2(s);
  }, [manual, pending]);

  /** FIFO portion that actually fits after manual reservations (oldest bills first). */
  const fifoPreview = useMemo(() => {
    if (!pending.length) return { fifoPlaced: 0, fifoUnplaced: 0 };
    const fifoRaw = round2(parseFloat(fifoAmount || "0"));
    const { allocations, remainingUnallocated } = allocateFIFOAfterManual(pending, manual, fifoRaw);
    const fifoPlaced = round2(allocations.reduce((sum, a) => sum + a.amount, 0));
    return { fifoPlaced, fifoUnplaced: remainingUnallocated };
  }, [pending, manual, fifoAmount]);

  const combinedTotal = useMemo(() => round2(manualTotal + fifoPreview.fifoPlaced), [manualTotal, fifoPreview.fifoPlaced]);

  /** Sum of original bill amounts and open balances for listed pending bills (this branch). */
  const pendingBillTotals = useMemo(() => {
    let totalBilled = 0;
    let totalBalance = 0;
    for (const b of pending) {
      totalBilled += round2(b.totalAmount);
      totalBalance += round2(b.balanceAmount);
    }
    return { totalBilled: round2(totalBilled), totalBalance: round2(totalBalance), count: pending.length };
  }, [pending]);

  const handleRecordPayment = async () => {
    if (!customerId) return toast.error("Select customer.");
    const fifoRaw = round2(parseFloat(fifoAmount || "0"));
    if (manualTotal <= 0 && fifoRaw <= 0) {
      return toast.error("Enter a FIFO amount and/or per-bill amounts.");
    }
    setSubmitting(true);
    try {
      await applyPaymentCombined(customerId, manual, fifoAmount, note, payMode, branch);
      toast.success("Payment recorded.");
      setFifoAmount("");
      await loadPending();
      onSuccess?.();
    } catch (e) {
      toast.error(e?.message || "Payment failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-xs leading-relaxed text-slate-600">
        <strong className="text-slate-800">Per-bill</strong> amounts are reserved first.{" "}
        <strong className="text-slate-800">FIFO</strong> then applies to what is still open on each bill, oldest invoice date first (old due before new).
        Use one or both in a single receipt.
      </p>

      <label className="block text-sm font-semibold text-slate-800">
        Customer
        <select
          value={customerId}
          onChange={(e) => setCustomerId(e.target.value)}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
        >
          <option value="">— Select —</option>
          {customers.map((c) => (
            <option key={c.id} value={c.id}>
              {c.partyName || c.name || c.id}
            </option>
          ))}
        </select>
      </label>

      <fieldset className="rounded-lg border border-slate-200 bg-white p-3">
        <legend className="px-1 text-sm font-semibold text-slate-800">Paid by</legend>
        <div className="mt-1 flex flex-wrap gap-4 text-sm">
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="radio"
              name="credit-pay-mode"
              checked={payMode === "cash"}
              onChange={() => setPayMode("cash")}
            />
            Cash
          </label>
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="radio"
              name="credit-pay-mode"
              checked={payMode === "card_upi"}
              onChange={() => setPayMode("card_upi")}
            />
            Card / UPI
          </label>
        </div>
      </fieldset>

      <label className="block text-sm font-semibold text-slate-800">
        Note (optional)
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          placeholder="Receipt ref, UPI ref…"
        />
      </label>

      <div className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-3">
        <label className="text-xs font-bold uppercase tracking-wide text-indigo-900/80">FIFO pool (oldest invoice first)</label>
        <p className="mt-0.5 text-[11px] text-slate-600">
          Applied after per-bill amounts, only up to remaining balance on each bill.
        </p>
        <input
          type="number"
          min="0"
          step="0.01"
          value={fifoAmount}
          onChange={(e) => setFifoAmount(e.target.value)}
          className="mt-2 w-full max-w-xs rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
          placeholder="0.00"
        />
      </div>

      <div className="rounded-lg border border-slate-200 bg-slate-50/80 p-3">
        <p className="text-xs font-bold uppercase tracking-wide text-slate-600">Manual (per bill)</p>
        {loading ? (
          <p className="mt-2 text-sm text-slate-600">Loading bills…</p>
        ) : !customerId ? (
          <p className="mt-2 text-sm text-slate-600">Select a customer to see pending bills.</p>
        ) : pending.length === 0 ? (
          <p className="mt-2 text-sm text-slate-600">No pending bills.</p>
        ) : (
          <>
            <p className="mt-2 flex flex-wrap items-baseline justify-between gap-2 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-700">
              <span className="font-semibold text-slate-800">Bill amounts (this list)</span>
              <span className="tabular-nums">
                <span className="text-slate-500">Total billed</span>{" "}
                <strong className="text-slate-900">
                  ₹{pendingBillTotals.totalBilled.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </strong>
                <span className="mx-2 text-slate-300">·</span>
                <span className="text-slate-500">Open balance</span>{" "}
                <strong className="text-indigo-900">
                  ₹{pendingBillTotals.totalBalance.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </strong>
                <span className="ml-1 text-slate-500">({pendingBillTotals.count} bill{pendingBillTotals.count === 1 ? "" : "s"})</span>
              </span>
            </p>
            <div className="mt-2 max-h-52 overflow-y-auto rounded border border-slate-100 bg-white">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 text-left text-xs uppercase text-slate-500">
                    <th className="px-2 py-2">Bill</th>
                    <th className="px-2 py-2 text-right" title="Days from invoice to today">
                      Cr. days
                    </th>
                    <th className="px-2 py-2 text-right">Balance</th>
                    <th className="px-2 py-2 text-right">Pay now</th>
                  </tr>
                </thead>
                <tbody>
                  {pending.map((b) => (
                    <tr key={b.id} className="border-t border-slate-100">
                      <td className="px-2 py-2 font-mono text-xs">{b.voucherNo}</td>
                      <td className="px-2 py-2 text-right tabular-nums font-medium text-slate-800">
                        {daysOutstandingForBill(b)}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums">₹{round2(b.balanceAmount).toLocaleString("en-IN")}</td>
                      <td className="px-2 py-2 text-right">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={manual[b.id] ?? ""}
                          onChange={(e) =>
                            setManual((prev) => ({
                              ...prev,
                              [b.id]: e.target.value,
                            }))
                          }
                          className="w-24 rounded border border-slate-300 px-2 py-1 text-right text-sm"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <div className="space-y-2 rounded-lg border border-slate-200 bg-white p-3 text-sm">
        <div className="flex flex-wrap justify-between gap-2 text-slate-700">
          <span>Manual subtotal</span>
          <span className="font-semibold tabular-nums">₹{manualTotal.toLocaleString("en-IN")}</span>
        </div>
        <div className="flex flex-wrap justify-between gap-2 text-slate-700">
          <span>FIFO applied (preview)</span>
          <span className="font-semibold tabular-nums">₹{fifoPreview.fifoPlaced.toLocaleString("en-IN")}</span>
        </div>
        {fifoPreview.fifoUnplaced > 0.009 && (
          <p className="rounded-md bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
            ₹{fifoPreview.fifoUnplaced.toLocaleString("en-IN", { minimumFractionDigits: 2 })} of the FIFO pool cannot be
            applied (not enough remaining balance across bills).
          </p>
        )}
        <div className="flex flex-wrap justify-between gap-2 border-t border-slate-200 pt-2 font-bold text-slate-900">
          <span>Total this receipt</span>
          <span className="tabular-nums">₹{combinedTotal.toLocaleString("en-IN")}</span>
        </div>
      </div>

      <button
        type="button"
        disabled={submitting || !customerId || (manualTotal <= 0 && round2(parseFloat(fifoAmount || "0")) <= 0)}
        onClick={handleRecordPayment}
        className="w-full rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {submitting ? "Recording…" : "Record payment"}
      </button>
    </div>
  );
}
